import { useState } from "react";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// apiClient/endpoint let this same form serve both the staff dashboard (default:
// lib/api.js -> /auth/change-password) and the client portal (clientApi -> /client/change-password).
export default function ChangePasswordForm({ onSuccess, submitLabel, apiClient = api, endpoint = "/auth/change-password" }) {
  const { t } = useLang();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error(t("portal.passwordsNoMatch"));
      return;
    }
    if (form.new_password.length < 6) {
      toast.error(t("portal.passwordTooShort"));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(endpoint, {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(t("portal.passwordChangedToast"));
      setForm({ current_password: "", new_password: "", confirm_password: "" });
      onSuccess?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.changePasswordError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.currentPassword")}</label>
        <input
          required
          type="password"
          value={form.current_password}
          onChange={upd("current_password")}
          className="input-dark"
          data-testid="change-password-current"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.newPassword")}</label>
          <input
            required
            minLength={6}
            type="password"
            value={form.new_password}
            onChange={upd("new_password")}
            className="input-dark"
            data-testid="change-password-new"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.confirmPassword")}</label>
          <input
            required
            minLength={6}
            type="password"
            value={form.confirm_password}
            onChange={upd("confirm_password")}
            className="input-dark"
            data-testid="change-password-confirm"
          />
        </div>
      </div>
      <button type="submit" disabled={submitting} className="btn-gold text-sm py-2 px-5" data-testid="change-password-submit">
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
        {submitLabel || t("portal.changePassword")}
      </button>
    </form>
  );
}
